import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ThemeToggleButton from './components/ThemeToggleButton';
import ScrollToTop from './components/ScrollToTop';
import BackToTop from './components/BackToTop';
import Feedback from './components/Feedback';
import SocialLinks from './components/SocialLinks';
import MailCopyButton from './components/MailCopyButton';
import Home from './pages/Home';
import Work from './pages/Work';
import CaseStudy from './pages/CaseStudy';
import Stack from './pages/Stack';
import SideProjects from './pages/SideProjects';
import BookCoverProject from './pages/BookCoverProject';
import { footerLinks } from './data/siteData';
import useScrollFade from './hooks/useScrollFade';
import './App.css';

function AppInner({ theme, toggleTheme }) {
  useScrollFade();
  const footerContentRef = useRef(null);
  // Stack (/stack) has its own fixed CTA in the same bottom-right corner
  // (see Stack.js/Stack.css) and doesn't otherwise want the shared site
  // footer at all — it's a full-viewport interactive canvas, not a
  // scrolling content page with a natural end to put a footer at.
  const hideFooter = useLocation().pathname === '/stack';

  // Feedback/ThemeToggleButton's mobile positions need to clear the footer's
  // actual visible content (the copyright text + icons row), not the
  // footer's own full padded box. An earlier pass measured the whole
  // <footer> element (padding: 2rem top and bottom around the content) and
  // used that as the required clearance — which was wrong: the footer's top
  // padding is empty space with nothing in it to overlap, so those buttons
  // were floating a full 2rem higher than they needed to just to clear space
  // that was never actually occupied. Measuring `.footer-container` (the
  // inner row that actually holds the text/icons) instead, and letting the
  // CSS add just the footer's own *bottom* padding (a fixed, known 2rem, not
  // something that needs measuring) on top of it, clears exactly the real
  // content with no wasted margin. getBoundingClientRect() (not the
  // ResizeObserver entry's own contentRect, which excludes this element's
  // padding — moot here since `.footer-container` has none, but kept for
  // consistency) gives the true border-box height. Depends on `hideFooter`
  // (not `[]`) now that the footer conditionally mounts/unmounts across
  // navigation — a footer-less route (Stack) unmounts the element the
  // original observer was watching, and since that observer instance never
  // gets re-pointed at whatever footer element mounts next on its own,
  // `--footer-content-height` would otherwise freeze at its last value the
  // first time any route toggled the footer off and back on.
  useEffect(() => {
    const el = footerContentRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--footer-content-height', `${el.getBoundingClientRect().height}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hideFooter]);

  return (
    <>
      <ScrollToTop />
      <BackToTop />
      <Feedback />
      <div className="App">
        <ThemeToggleButton theme={theme} toggleTheme={toggleTheme} />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/:id" element={<CaseStudy />} />
            <Route path="/stack" element={<Stack />} />
            <Route path="/side-projects" element={<SideProjects />} />
            <Route path="/side-projects/book-cover" element={<BookCoverProject />} />
          </Routes>
        </main>
        {!hideFooter && (
          <footer className="footer">
            <div className="page-container footer-container" ref={footerContentRef}>
              <p>© 2026 Joshua Yoon</p>
              <div className="footer-icons">
                <MailCopyButton />
                <SocialLinks links={footerLinks} />
              </div>
            </div>
          </footer>
        )}
      </div>
    </>
  );
}

function App() {
  const [theme, setTheme] = useState(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );

  // Apply theme to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Follow system preference changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <Router>
      <AppInner theme={theme} toggleTheme={toggleTheme} />
    </Router>
  );
}

export default App;
